import {
  Box,
  Grid,
  Link,
  Stack,
  Typography,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Tooltip,
} from "@mui/material";
import { PagePaper, SectionPaper } from "@/components/Paper";
import { PageTitle } from "@/components/Title";
import { FeedGrid } from "../components/AllMattersDetailFeedGrid";
import { KeyboardArrowLeft } from "@mui/icons-material";
import React, { useState } from "react";
import { useApiClient } from "@/hooks/useApiClient";
import { useParams } from "react-router-dom";
import useSWR from "swr";
import { isRequestCompleted, statusToLabel } from "@/features/allMatters/utils";
import { type RequestDetailAndRealEstates } from "@/features/allMatters/types";
import CircularProgress from "@mui/material/CircularProgress";
import LibraryBooksIcon from "@mui/icons-material/LibraryBooks";
import { format } from "date-fns";
import { AllMattersEditModal } from "@/features/allMatters/components/AllMattersEditModal";
import { toast } from "react-toastify";
import { ContactUsAlert } from "@/components/ContactUsAlert";
import * as Sentry from "@sentry/react";
import { z } from "zod";
import { formatNumberWithComma } from "@/utils/formatNumber";

const allMattersAnalyzeDetailContractSchema = z
  .object({
    isOtherUserResource: z.boolean(),
    hasTriggerPictureRequest: z.boolean(),
  })
  .passthrough();

/**
 * 全部事項解析結果詳細APIのレスポンスが契約を満たしているか検証する。
 * @param payload APIレスポンスのペイロード
 * @param endpoint 呼び出し元のAPIエンドポイント
 * @throws {Error} API契約違反がある場合に例外を送出する
 */
const validateAllMattersAnalyzeDetailContract = (
  payload: unknown,
  endpoint: string
): void => {
  const parsed = allMattersAnalyzeDetailContractSchema.safeParse(payload);
  if (parsed.success) return;

  const error = new Error(`API contract violation at ${endpoint}`);
  Sentry.captureException(error, {
    extra: {
      endpoint,
      issues: parsed.error.issues,
    },
  });
  throw error;
};

export const AllMattersDetail: React.FC = () => {
  const { apiClient } = useApiClient();
  const { id } = useParams();
  const identificationNameDisabledMessage =
    "他ユーザーが設定した識別名は編集できません";
  const [openAllMattersEditDialog, setOpenAllMattersEditDialog] =
    useState<boolean>(false);
  const [showMethodType, setShowMethodType] = useState(false);
  const fetcher = async (url: string): Promise<RequestDetailAndRealEstates> => {
    const { data } = await apiClient.get<RequestDetailAndRealEstates>(url);
    validateAllMattersAnalyzeDetailContract(data, url);
    return data;
  };

  const { data, error, mutate } = useSWR<RequestDetailAndRealEstates, Error>(
    id ? `/all-matters-analyze/${id}` : null,
    fetcher,
    {
      revalidateOnFocus: true,
      refreshInterval: 60000,
      dedupingInterval: 30000,
    }
  );

  if (error != null && data === undefined) {
    return (
      <PagePaper>
        <Stack alignItems="center">
          <Typography color="error" sx={{ my: "20px" }}>
            データの取得に失敗しました。
          </Typography>
        </Stack>
      </PagePaper>
    );
  }

  if (data == null) {
    return <CircularProgress />;
  }

  const datetimeFormatter = (datetime: string): string => {
    return format(new Date(datetime), "yyyy/MM/dd HH:mm");
  };

  // 識別名を更新後、すぐにデータの再フェッチを行う
  const handleRefreshAfterEdit = (): void => {
    mutate(data).catch(() => {
      toast.error("データの再取得に失敗しました。");
    });
  };

  return (
    <PagePaper>
      <form>
        <Grid container sx={{ mb: 2 }} rowSpacing={1}>
          <Grid item xs={12}>
            <Grid container>
              <Grid item xs={12}>
                {/* ページタイトル */}
                <Box sx={{ display: "flex", mb: 3 }}>
                  <LibraryBooksIcon sx={{ mr: 1 }} fontSize="large" />
                  <PageTitle>全部事項データ化詳細</PageTitle>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <ContactUsAlert />
              </Grid>
              <Grid item xs={12}>
                <Table size="small" sx={{ mb: 2, ml: 1 }}>
                  <TableBody>
                    <TableRow>
                      <TableCell
                        component="th"
                        sx={{
                          width: "10%",
                          border: "none",
                          p: 1,
                          pl: 0,
                          pr: 0,
                        }}
                      >
                        ステータス
                      </TableCell>
                      <TableCell sx={{ border: "none", p: 1, pl: 0 }}>
                        {statusToLabel(data?.status)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        component="th"
                        sx={{
                          width: "10%",
                          border: "none",
                          p: 1,
                          pl: 0,
                          pr: 0,
                        }}
                      >
                        件数
                      </TableCell>
                      <TableCell sx={{ border: "none", p: 1, pl: 0 }}>
                        {formatNumberWithComma(data?.details.length)}件（成功：
                        {formatNumberWithComma(
                          data?.details.filter(
                            (detail) => detail.status === "succeeded"
                          ).length
                        )}
                        件、 失敗：
                        {formatNumberWithComma(
                          data?.details.filter(
                            (detail) => detail.status === "failed"
                          ).length
                        )}
                        件）
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        component="th"
                        sx={{
                          width: "10%",
                          border: "none",
                          p: 1,
                          pl: 0,
                          pr: 0,
                        }}
                      >
                        識別番号
                      </TableCell>
                      <TableCell sx={{ border: "none", p: 1, pl: 0 }}>
                        {data?.taskId}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        component="th"
                        sx={{
                          width: "10%",
                          border: "none",
                          p: 1,
                          pl: 0,
                          pr: 0,
                        }}
                      >
                        識別名
                      </TableCell>
                      <TableCell sx={{ border: "none", p: 1, pl: 0 }}>
                        {data?.identificationName}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        component="th"
                        sx={{
                          width: "10%",
                          border: "none",
                          p: 1,
                          pl: 0,
                          pr: 0,
                        }}
                      >
                        依頼日時
                      </TableCell>
                      <TableCell sx={{ border: "none", p: 1, pl: 0 }}>
                        {datetimeFormatter(data?.createdAt)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        component="th"
                        sx={{
                          width: "10%",
                          border: "none",
                          p: 1,
                          pl: 0,
                          pr: 0,
                        }}
                      >
                        解析オプション
                      </TableCell>
                      <TableCell sx={{ border: "none", p: 1, pl: 0 }}>
                        {[
                          data?.includeTitleSection && "表題部",
                          data?.includeKoku && "甲区",
                          data?.includeOtsuku && "乙区",
                        ]
                          .filter(Boolean)
                          .join(", ") || "-"}
                      </TableCell>
                    </TableRow>
                    {!showMethodType ? (
                      <TableRow>
                        <TableCell
                          sx={{
                            border: "none",
                            p: 1,
                            pl: 0,
                            pr: 0,
                          }}
                        >
                          <Typography
                            variant="body2"
                            onClick={() => {
                              setShowMethodType(true);
                            }}
                            sx={{
                              color: "text.secondary",
                              cursor: "pointer",
                            }}
                          >
                            解析方法を表示
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ border: "none", p: 1, pl: 0 }} />
                      </TableRow>
                    ) : (
                      <TableRow>
                        <TableCell
                          component="th"
                          sx={{
                            width: "10%",
                            border: "none",
                            p: 1,
                            pl: 0,
                            pr: 0,
                          }}
                        >
                          解析方法
                        </TableCell>
                        <TableCell sx={{ border: "none", p: 1, pl: 0 }}>
                          {data?.analyzeMethodType === "llm_api"
                            ? "AI解析"
                            : data?.analyzeMethodType === "rule_based"
                            ? "通常解析"
                            : "-"}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Grid>
              <Grid item xs={12} sx={{ textAlign: "left" }}>
                <Tooltip
                  title={
                    data?.isOtherUserResource
                      ? identificationNameDisabledMessage
                      : ""
                  }
                  placement="top"
                  arrow
                  disableHoverListener={!data?.isOtherUserResource}
                >
                  <span>
                    <Button
                      variant="outlined"
                      component={Link}
                      onClick={() => {
                        setOpenAllMattersEditDialog(true);
                      }}
                      disabled={data?.isOtherUserResource}
                      sx={{
                        textAlign: "center",
                        fontWeight: "bold",
                      }}
                    >
                      識別名編集
                    </Button>
                  </span>
                </Tooltip>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <SectionPaper>
          <FeedGrid
            row={data?.details}
            requestId={data?.id}
            disabledAction={
              !isRequestCompleted(data?.status) || data?.succeededCount === 0
            }
            isPropertyDetailOptionCredit={data?.isPropertyDetailOptionCredit}
            hasTriggerPictureRequest={data?.hasTriggerPictureRequest}
            succeededCount={data?.succeededCount}
            onDownloadComplete={() => {
              mutate(data).catch(() => {
                toast.error("データの再取得に失敗しました。");
              });
            }}
          />
          <Box sx={{ mb: 6 }} />
          <Stack
            direction={"row"}
            spacing={4}
            justifyContent={"center"}
            mb={2}
          ></Stack>
        </SectionPaper>

        {/* 戻る */}
        <Box sx={{ mt: 2 }}>
          <Link href="/allmatters/list" sx={{ display: "inline-block" }}>
            <Box sx={{ display: "flex" }}>
              <KeyboardArrowLeft />
              <Typography>一覧に戻る</Typography>
            </Box>
          </Link>
        </Box>
      </form>
      {openAllMattersEditDialog && !data?.isOtherUserResource && (
        <AllMattersEditModal
          open={openAllMattersEditDialog}
          setOpen={setOpenAllMattersEditDialog}
          handleClose={handleRefreshAfterEdit}
          pictureRequestId={Number(id)}
          data={data}
        />
      )}
    </PagePaper>
  );
};
